import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from './cn';

/**
 * Numbered pages under a list: ‹ 1 … 4 5 6 … 20 ›.
 *
 * Shows the first and last page, the current one and its neighbours, and an
 * ellipsis for the gaps, so the bar stays the same width however long the
 * list grows. "Showing 11–20 of 342" says where the reader is in the whole.
 */
export interface PaginationProps {
  /** 1-based. */
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
  /** Disables the buttons while a page is loading. */
  disabled?: boolean;
  className?: string;
}

/** The page numbers to show, with `null` for an ellipsis. */
export function pageWindow(page: number, pages: number): Array<number | null> {
  if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
  const keep = new Set([1, pages, page - 1, page, page + 1]);
  // Near an end, show enough numbers that the bar does not shrink.
  if (page <= 3) [2, 3, 4].forEach((n) => keep.add(n));
  if (page >= pages - 2) [pages - 3, pages - 2, pages - 1].forEach((n) => keep.add(n));
  const sorted = [...keep].filter((n) => n >= 1 && n <= pages).sort((a, b) => a - b);
  const out: Array<number | null> = [];
  sorted.forEach((n, i) => {
    if (i > 0 && n - sorted[i - 1]! > 1) out.push(null);
    out.push(n);
  });
  return out;
}

export function Pagination({ page, pageSize, total, onPageChange, disabled, className }: PaginationProps) {
  const pages = Math.max(1, Math.ceil(total / pageSize));
  if (total === 0) return null;
  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);

  const btn = 'inline-flex h-8 min-w-8 items-center justify-center rounded-btn px-2 text-small tabular-nums max-md:h-10 max-md:min-w-10 disabled:cursor-not-allowed disabled:opacity-40';

  return (
    <nav aria-label="Pages" className={cn('flex flex-wrap items-center justify-between gap-2', className)}>
      <p className="text-caption text-neutral-600">
        Showing <span className="tabular-nums">{first}–{last}</span> of <span className="tabular-nums">{total.toLocaleString('en-IN')}</span>
      </p>
      {pages > 1 && (
        <ul className="flex flex-wrap items-center gap-1">
          <li>
            <button
              type="button"
              className={cn(btn, 'text-neutral-700 hover:bg-neutral-100')}
              onClick={() => onPageChange(page - 1)}
              disabled={disabled || page <= 1}
              aria-label="Previous page"
            >
              <ChevronLeft aria-hidden className="h-4 w-4" />
            </button>
          </li>
          {pageWindow(page, pages).map((n, i) => (
            <li key={n ?? `gap-${i}`}>
              {n === null ? (
                <span className="inline-flex h-8 min-w-6 items-center justify-center text-small text-neutral-500" aria-hidden>…</span>
              ) : (
                <button
                  type="button"
                  className={cn(
                    btn,
                    n === page
                      ? 'bg-primary-600 font-semibold text-white'
                      : 'text-neutral-700 hover:bg-neutral-100',
                  )}
                  onClick={() => onPageChange(n)}
                  disabled={disabled && n !== page}
                  aria-label={`Page ${n}`}
                  aria-current={n === page ? 'page' : undefined}
                >
                  {n}
                </button>
              )}
            </li>
          ))}
          <li>
            <button
              type="button"
              className={cn(btn, 'text-neutral-700 hover:bg-neutral-100')}
              onClick={() => onPageChange(page + 1)}
              disabled={disabled || page >= pages}
              aria-label="Next page"
            >
              <ChevronRight aria-hidden className="h-4 w-4" />
            </button>
          </li>
        </ul>
      )}
    </nav>
  );
}
